import './styles.scss';
import { useEffect, useRef, useState } from "react";
import { Spotip17anFunctions, saveProfilePhoto, getPlaylistPosterUrl, getArtistAlbums } from "../../../utils/api";
import { SongList } from "../spotipitan/song-list";
import { SongEditModal } from '../../shared/song-edit-modal';
import useUserStore, { User, DiscordUser } from "../../../store/UserState";
import { Song } from "../spotipitan/types";
import { AlbumDTO } from "../../../utils/apiTypes";
import { useNavigate } from "react-router-dom";
import { DEFAULT_USER_PHOTO, DISCORD_CLIENT_ID } from '../../../utils/constants';
import LocalStorage from '../../../utils/local-storage';
import { DiscordBotApi, DiscordVoiceStatus } from '../../../utils/discordBotApi';
import { auth } from '../../../firebase';
import { toast } from "react-hot-toast";
import classNames from 'classnames';
import SeparatorLine from '../../shared/separator-line/separator-line';
import { LoginHome } from '../../users';
import { AlbumEditModal } from '../../shared/album-edit-modal';
import ItemsHorizontalScroll from '../../squadDiskShared/itemsHorizontalScroll/itemsHorizontalScroll';
import ItemDetailsToView from '../../squadDiskShared/itemDetailsView/itemDetailsToView';
import SquareImage from '../../shared/square-image/square-image';
import { isMobileDevice } from '../../../utils/device-utils';

const MyProfileView = () => {
    const { state, loadUser } = useUserStore(state => state);
    const { user } = state;
    const navigate = useNavigate();
    const [userSongs, setUserSongs] = useState<Song[] | undefined>(undefined);
    const [userAlbums, setUserAlbums] = useState<AlbumDTO[] | undefined>(undefined);
    const [spotip17anUser, setSpotip17anUser] = useState<User | undefined>();
    const [showAlbumModal, setShowAlbumModal] = useState(false);
    const [selectedAlbumToEdit, setSelectedAlbumToEdit] = useState<AlbumDTO | null>(null);
    const [newDisplayName, setNewDisplayName] = useState(user?.displayName || "");
    const [profilePhoto, setProfilePhoto] = useState<string>(user?.photoURL || DEFAULT_USER_PHOTO);
    const [isEditingName, setIsEditingName] = useState(false); // State for editing mode
    const [showCreateSongModal, setShowCreateSongModal] = useState(false);

    const discordUser = useUserStore(state => state.state.discordUser);
    const setDiscordUser = useUserStore(state => state.setDiscordUser);
    const [voiceStatus, setVoiceStatus] = useState<DiscordVoiceStatus | null>(null);
    const [loadingVoice, setLoadingVoice] = useState<boolean>(false);

    const fetchVoiceStatus = async (userId: string) => {
        setLoadingVoice(true);
        try {
            const status = await DiscordBotApi.getUserVoiceStatus(userId);
            setVoiceStatus(status);
        } catch (error) {
            console.error("Error fetching voice status:", error);
            setVoiceStatus(null);
        } finally {
            setLoadingVoice(false);
        }
    };

    useEffect(() => {
        const getDiscordProfile = Spotip17anFunctions.use('getDiscordProfile');
        getDiscordProfile.post().then((res) => {
            if (res && res.discord_id) {
                const discordUserObj: DiscordUser = {
                    id: res.discord_id,
                    username: res.discord_username || "",
                    global_name: res.discord_global_name || null,
                    avatar: res.discord_avatar || null,
                };
                setDiscordUser(discordUserObj);
            } else {
                setDiscordUser(null);
            }
        }).catch((err) => {
            console.error("Error fetching Discord profile on profile mount:", err);
        });
    }, []);

    useEffect(() => {
        if (discordUser?.id) {
            fetchVoiceStatus(discordUser.id);
        } else {
            setVoiceStatus(null);
        }
    }, [discordUser?.id]);

    const handleConnectDiscord = () => {
        if (!DISCORD_CLIENT_ID) {
            toast.error("El Client ID de Discord no está configurado en constants.ts");
            return;
        }
        const redirectUri = window.location.origin + window.location.pathname;
        const discordAuthUrl = `https://discord.com/api/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&redirect_uri=${encodeURIComponent(
            redirectUri
        )}&response_type=token&scope=identify`;
        window.location.href = discordAuthUrl;
    };

    const handleDisconnectDiscord = () => {
        setDiscordUser(null);
        setVoiceStatus(null);

        // Clear in backend database
        const disconnectDiscord = Spotip17anFunctions.use('disconnectDiscord');
        disconnectDiscord.post().catch((err) => {
            console.error("Error unlinking Discord on backend:", err);
        });

        toast.success("Cuenta de Discord desvinculada");
    };

    const handleCopyDiscordId = () => {
        if (discordUser?.id) {
            navigator.clipboard.writeText(discordUser.id);
            toast.success("¡ID de Discord copiado al portapapeles!");
        }
    };

    const handleRefreshVoiceStatus = () => {
        if (discordUser?.id) {
            fetchVoiceStatus(discordUser.id);
        }
    };



    const fetchUserAlbums = (artistId: number | string) => {
        getArtistAlbums(artistId).then((albums) => {
            setUserAlbums(albums);
        }).catch(() => {
            setUserAlbums([]);
        });
    };

    const fetchUserSongs = () => {
        if (!user?.uid) {
            return;
        }

        const getSpotip17anData = Spotip17anFunctions.use('getSpotip17anData');
        getSpotip17anData.call().then((data) => {
            setUserSongs(data.songs);
            setSpotip17anUser(data.user);
            if (data?.user?.spotip17an_artist) {
                fetchUserAlbums(data.user.id || user.uid);
            }
        }).catch((error) => {
            setUserSongs(undefined);
            setSpotip17anUser(undefined);
        });
    }

    const handleUpdateDisplayName = async () => {
        if (!isEditingName || !user?.uid || !newDisplayName.trim()) return;
        try {
            await user.updateProfile({ displayName: newDisplayName });
            toast.success("Nombre actualizado correctamente");
            setIsEditingName(false); // Exit edit mode
        } catch (error) {
            console.error("Error updating display name:", error);
            toast.error("Error al actualizar el nombre");
        }
    };

    const handleUploadProfilePhoto = async (file: File) => {
        if (!user?.uid) return;
        const formData = new FormData();
        formData.append("file", file);
        try {
            setProfilePhoto(DEFAULT_USER_PHOTO);
            const response = await saveProfilePhoto(formData);
            const photoUrl = await getPlaylistPosterUrl({ id: user.uid, poster_url: response.url });
            await user.updateProfile({ photoURL: photoUrl });
            const blob = await fetch(photoUrl).then(res => res.blob());
            setProfilePhoto(URL.createObjectURL(blob));
            toast.success("Foto de perfil actualizada correctamente");
        } catch (error) {
            console.error("Error uploading profile photo:", error);
            toast.error("Error al actualizar la foto de perfil");
        }
    };

    useEffect(() => {
        if (user?.uid) {
            fetchUserSongs();
        }
    }, [user?.uid]);

    if (!user) {
        return <div>
            <LoginHome />
        </div>;
    }

    const handleOnUploadSong = () => {
        fetchUserSongs();
        setShowCreateSongModal(false);
    };

    return (
        <div className="my-profile-view container">
            <div className="my-profile-view__header">
                <label htmlFor="profile-photo-input" className="my-profile-view__avatar-wrapper">
                    <img src={profilePhoto} alt="User Avatar" className="my-profile-view__avatar" />
                </label>
                <input
                    id="profile-photo-input"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => e.target.files && handleUploadProfilePhoto(e.target.files[0])}
                />
                <div className="my-profile-view__display-name-wrapper">
                    <input
                        id='display-name-input'
                        type="text"
                        className="my-profile-view__display-name"
                        value={newDisplayName}
                        readOnly={!isEditingName}
                        onChange={(e) => setNewDisplayName(e.target.value)}
                        onBlur={handleUpdateDisplayName}
                        placeholder="Nuevo nombre de usuario"
                    />
                    <button
                        hidden={isEditingName}
                        className={classNames("my-profile-view__edit-button", {
                            "editing": isEditingName
                        })}
                        onClick={() => {
                            setIsEditingName(!isEditingName);
                            document.getElementById('display-name-input')?.focus();
                        }}
                    >
                        <i className="fa-solid fa-pencil"></i>
                    </button>
                </div>
            </div>
            <div className="my-profile-view__email">
                <i className="fa-solid fa-envelope"></i> {user.email}
            </div>

            {/* Discord Integration Section */}
            <div className="my-profile-view__discord">
                <SeparatorLine style={{
                    marginTop: '20px',
                    marginBottom: '20px',
                }} />
                <h5 className="my-profile-view__discord-title">
                    <i className="fa-brands fa-discord"></i> Vincular Discord
                </h5>
                {discordUser ? (
                    <div className="discord-card">
                        <div className="discord-card__profile">
                            {discordUser.avatar ? (
                                <img
                                    src={discordUser.avatar.startsWith('http') ? discordUser.avatar : `https://cdn.discordapp.com/avatars/${discordUser.id}/${discordUser.avatar}.png`}
                                    alt="Discord Avatar"
                                    className="discord-card__avatar"
                                />
                            ) : (
                                <div className="discord-card__avatar-placeholder">
                                    <i className="fa-brands fa-discord"></i>
                                </div>
                            )}
                            <div className="discord-card__info">
                                <span className="discord-card__username">
                                    {discordUser.global_name || discordUser.username}
                                </span>
                                <span className="discord-card__tag">@{discordUser.username}</span>
                            </div>
                        </div>

                        <button className="discord-card__btn-disconnect" onClick={handleDisconnectDiscord}>
                            Desconectar
                        </button>
                    </div>
                ) : (
                    <button className="discord-connect-btn" onClick={handleConnectDiscord}>
                        <i className="fa-brands fa-discord"></i> Conectar Discord
                    </button>
                )}
            </div>

            {spotip17anUser?.spotip17an_artist && (
                <div className="my-profile-view__songs pb-4">
                    <SeparatorLine style={{
                        marginTop: '0',
                        marginBottom: '30px',
                    }} />
                    <div className='d-flex justify-content-center align-items-center gap-3 mb-4'>
                        <button
                            className="nice-button"
                            id="button-upload-song-modal"
                            onClick={() => setShowCreateSongModal(true)}
                        >
                            <span>Subir canción&nbsp;</span>
                            <i className="fa-solid fa-music" aria-hidden="true"></i>
                        </button>
                        <button
                            className="nice-button"
                            onClick={() => {
                                setSelectedAlbumToEdit(null);
                                setShowAlbumModal(true);
                            }}
                        >
                            <span>Crear álbum&nbsp;</span>
                            <i className="fa-solid fa-compact-disc" aria-hidden="true"></i>
                        </button>
                    </div>

                    {userAlbums && userAlbums.length > 0 && (
                        <div className="mb-4">
                            <div className="mb-3">
                                <h5>Tus álbumes:</h5>
                            </div>
                            <div style={{ height: '175px' }}>
                                <ItemsHorizontalScroll className="container spotipitan-home__items-container">
                                    {userAlbums.map((album) => (
                                        <ItemDetailsToView
                                            key={album.id}
                                            description={`${album.songs_count ?? album.songs?.length ?? 0} canciones`}
                                            title={album.title || album.username || album.name || "Álbum"}
                                            onClick={() => navigate(`/playlist/${album.id}`)}
                                            showHover={true}
                                            iconClass=""
                                            image={album.cover_url || album.poster_url ? (
                                                <SquareImage playlist={album as any} />
                                            ) : undefined}
                                        />
                                    ))}
                                </ItemsHorizontalScroll>
                            </div>
                        </div>
                    )}

                    <div className="mb-3">
                        <h5>Tus canciones:</h5>
                    </div>
                    <SongList songs={userSongs ?? []} loadingSongs={userSongs === undefined} />
                    <SongEditModal
                        show={showCreateSongModal}
                        song={null}
                        onClose={() => setShowCreateSongModal(false)}
                        onSave={handleOnUploadSong}
                    />
                    <AlbumEditModal
                        show={showAlbumModal}
                        album={selectedAlbumToEdit}
                        onClose={() => {
                            setShowAlbumModal(false);
                            setSelectedAlbumToEdit(null);
                        }}
                        onSave={(newOrUpdated) => {
                            if (spotip17anUser?.id || user?.uid) {
                                fetchUserAlbums(spotip17anUser?.id || user.uid);
                            }
                        }}
                        onDelete={() => {
                            if (spotip17anUser?.id || user?.uid) {
                                fetchUserAlbums(spotip17anUser?.id || user.uid);
                            }
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default MyProfileView;
